import {
  SubscriptionEvent,
  SubscriptionEventPlan,
  SubscriptionEventType,
} from 'types/interfaces/admin/analytics';
import { formatAdminDate } from 'utils/formatAdminDate';

/** Chip text per event type. Trial events carry the "Trial" prefix. */
export const EVENT_LABELS: Record<SubscriptionEventType, string> = {
  signup: 'Signup',
  cancellation: 'Cancellation',
  trial_start: 'Trial start',
  trial_stop: 'Trial stop',
};

/** Chip colour follows the chart legends: something starting is green, something ending is red. */
export const EVENT_TONES: Record<SubscriptionEventType, 'positive' | 'negative'> = {
  signup: 'positive',
  cancellation: 'negative',
  trial_start: 'positive',
  trial_stop: 'negative',
};

export const PLAN_LABELS: Record<SubscriptionEventPlan, string> = {
  elite: 'Elite',
  eternal_durdles_trial: 'Eternal Durdles trial',
};

/** The column headers, in order. */
export const SUBSCRIPTION_EVENT_COLUMNS = [
  'Event',
  'Username',
  'Email',
  'Plan',
  'Event date',
  'Elite since',
  'Last subscription event',
] as const;

/**
 * The muted second line under a row: only the extra facts the backend sent.
 * Null when there are none, so the row has no second line.
 */
export const subscriptionEventDetailsLine = (details: SubscriptionEvent['details']): string | null => {
  if (!details) return null;
  const parts: string[] = [];
  if (details.cancelReason) parts.push(`Cancel reason: ${details.cancelReason}`);
  if (details.source) parts.push(`Source: ${details.source}`);
  if (details.referredByUsername) parts.push(`Referred by ${details.referredByUsername}`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/** A nullable admin timestamp: the shared UTC format, or a dash when there is none. */
export const formatOptionalAdminDate = (iso: string | null | undefined): string => (iso ? formatAdminDate(iso) : '—');

/**
 * True when the endpoint does not exist (a backend that predates it). An
 * unmatched route answers 404 with an HTML body, which RTK Query reports as a
 * PARSING_ERROR carrying originalStatus 404, so both spellings are checked.
 * Only this case reads as "no events"; every other failure is an error.
 */
export const isMissingEndpointError = (error: unknown): boolean => {
  const e = error as { status?: unknown; originalStatus?: unknown } | null | undefined;
  return e?.status === 404 || e?.originalStatus === 404;
};
